import React from 'react';
import classNames from 'classnames';
import StandardTableSubHeader from './StandardTableSubHeader';
import useWindowSize from '@hooks/useWindowSize';
import type { StatColumnGroup } from '@lib/page-utils/Opta/columns';

const MOBILE_BREAKPOINT = 980;

/**
 * Subtle vertical band tint that ties a main-header group to its sub-header
 * and body cells beneath. Even-indexed groups carry the tint, odd groups stay
 * transparent — the alternation reads as soft columns of association without
 * shouting.
 */
export const isBandedGroup = (groupIndex: number): boolean =>
  groupIndex % 2 === 0;

const headerBandClass = (groupIndex: number): string =>
  isBandedGroup(groupIndex) ? 'bg-primary-50/50' : 'bg-transparent';

const MainHeader = ({
  text,
  colSpan,
  bandClass,
  withDivider
}: {
  text: string;
  colSpan: number;
  bandClass: string;
  withDivider?: boolean;
}) => (
  <th
    scope="col"
    colSpan={colSpan}
    className={classNames(
      bandClass,
      'px-4 pt-4 pb-2 text-center align-bottom',
      'text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-600',
      withDivider && 'border-l border-neutral-200'
    )}
  >
    {text}
  </th>
);

type TableHeadersProps = {
  format: 'players' | 'teams';
  groups: StatColumnGroup[];
  sort: string;
  sortDirection: 'asc' | 'desc';
  setSort: (sortKey: string) => void;
};

const TableHeaders = ({
  format,
  groups,
  sort,
  sortDirection,
  setSort
}: TableHeadersProps) => {
  const { width } = useWindowSize();
  const isDesktop = width > MOBILE_BREAKPOINT;

  const visibleGroups = groups
    .filter((group) => isDesktop || !group.hideOnMobile)
    .map((group) => ({
      ...group,
      columns: group.columns.filter(
        (column) => isDesktop || !column.hideOnMobile
      )
    }))
    .filter((group) => group.columns.length > 0);

  const leadingMetaColumns: {
    label: string;
    title: string;
    sortKey?: string;
  }[] = !isDesktop
    ? []
    : format === 'players'
    ? [
        { label: '£', title: 'Price' },
        {
          label: 'Apps',
          title: 'Appearances',
          sortKey: 'participation.appearances'
        },
        {
          label: 'Mins',
          title: 'Minutes played',
          sortKey: 'participation.minutes'
        }
      ]
    : [
        {
          label: 'GP',
          title: 'Games played',
          sortKey: 'participation.gamesPlayed'
        }
      ];

  return (
    <thead>
      <tr>
        <th
          scope="col"
          className="sticky left-0 z-20 bg-neutral-50 px-4 pt-4 pb-2 text-left"
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
            {format === 'players' ? 'Player' : 'Team'}
          </span>
        </th>
        {leadingMetaColumns.length > 0 && (
          <th
            scope="col"
            aria-hidden
            colSpan={leadingMetaColumns.length}
            className="bg-neutral-50 px-4 pt-4 pb-2"
          />
        )}
        {visibleGroups.map((group, index) => (
          <MainHeader
            key={`${group.label}-${index}`}
            text={group.label}
            colSpan={group.columns.length}
            bandClass={headerBandClass(index)}
            withDivider
          />
        ))}
      </tr>
      <tr>
        <th
          scope="col"
          className={classNames(
            'sticky left-0 z-20 bg-neutral-50 px-4 pb-3 text-left',
            'border-b border-neutral-200'
          )}
        >
          <span
            className={classNames(
              'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]',
              'text-neutral-600'
            )}
          >
            Name
          </span>
        </th>
        {leadingMetaColumns.map((column) =>
          column.sortKey ? (
            <StandardTableSubHeader
              key={column.label}
              sort={sort}
              sortDirection={sortDirection}
              setSort={setSort}
              label={column.label}
              value={column.sortKey}
              title={column.title}
              bandClass="bg-neutral-50"
            />
          ) : (
            <th
              key={column.label}
              scope="col"
              title={column.title}
              className={classNames(
                'bg-neutral-50 px-3 py-2.5 text-center align-bottom',
                'border-b border-neutral-200',
                'text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-600'
              )}
            >
              {column.label}
            </th>
          )
        )}
        {visibleGroups.map((group, groupIndex) => (
          <React.Fragment key={`${group.label}-${groupIndex}`}>
            {group.columns.map((column, columnIndex) => (
              <StandardTableSubHeader
                key={`${column.path}-${columnIndex}`}
                sort={sort}
                sortDirection={sortDirection}
                setSort={setSort}
                label={column.label}
                value={column.sortKey ?? column.path}
                title={column.title}
                bandClass={headerBandClass(groupIndex)}
                withLeftDivider={columnIndex === 0}
              />
            ))}
          </React.Fragment>
        ))}
      </tr>
    </thead>
  );
};

export default TableHeaders;
